import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { getPartnerName, Partner } from 'features/settings/importMapping/partner';
import { Button, Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import AsyncButton from 'common/ui/AsyncButton';
import BidIntegration from './bidIntegration';
import partnerIntegrationService from './partnerIntegrationService';

export interface ManageBidIntegrationProps {
    bidIntegration: BidIntegration;
    partners: Partner[];
    onSave(bidIntegration: BidIntegration): Promise<any>;
    onRemove(): Promise<any>;
}

const partnersWithBidIntegration = [Partner.Barchart, Partner.DTN, Partner.EverAg, Partner.Agvance];

const ManageBidIntegration: React.FC<ManageBidIntegrationProps> = props => {
    
    const [bidIntegration, setBidIntegration] = useState<BidIntegration>(null);
    const [supportedPartners, setSupportedPartners] = useState<Partner[]>(null);
    const [isNew, setIsNew] = useState(false);

    useEffect(() => {
        setSupportedPartners(_.intersection(partnersWithBidIntegration, props.partners));
    }, [props.partners]);

    useEffect(() => {
        setBidIntegration(props.bidIntegration);
        setIsNew(props.bidIntegration == null);
    }, [props.bidIntegration]);

    function handleAdd() {
        setBidIntegration({
            ...new BidIntegration(),
            isEnabled: false,
            partner: supportedPartners.length === 1 ? supportedPartners[0] : null
        })
    }

    function handleSave(): Promise<any> {
        return props.onSave(bidIntegration);
    }

    function handleCancel(): void {
        setBidIntegration(props.bidIntegration);
    }

    function handleRemove(): void {
        props.onRemove();
    }

    return <IntegrationDetails
        bidIntegration={bidIntegration}
        partners={supportedPartners}
        startEditing={handleAdd}
        update={setBidIntegration}
        onSave={handleSave}
        onCancel={handleCancel}
        onRemove={handleRemove}
        isNew={isNew} />
}

interface IntegrationDetailsProps {
    bidIntegration: BidIntegration;
    partners: Partner[];
    startEditing(): void;
    update(integration: BidIntegration): void;
    onSave(): Promise<any>;
    onCancel(): void;
    onRemove(): void;
    isNew: boolean;
}

const IntegrationDetails: React.FC<IntegrationDetailsProps> = props => {

    const [hasChanges, setHasChanges] = useState(false);

    async function update(integration: BidIntegration) {
        props.update(integration);
        setHasChanges(true);
    }

    function canSave(): boolean {
        return props.bidIntegration?.partner != null
    }

    async function onSave(): Promise<any> {
        await props.onSave();
        setHasChanges(false);
    }

    function onCancel() {
        props.onCancel();
        setHasChanges(false);
    }

    async function onImportBids(): Promise<any> {
        await partnerIntegrationService.importBids(props.bidIntegration.merchantId)
    }

    if (props.partners?.length === 0) {
        return <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Cash Bids</Card.Title>
                    <Card.Text>No configured partners allow for Cash Bid integration</Card.Text>
                </Card.Body>
            </Card>;
    }

    return <Card className="mb-4"> 
        <Card.Body>
            <Card.Title>
                Cash Bids
                {props.bidIntegration == null && 
                    <button className="link-button" onClick={() => props.startEditing()}><i className="material-icons">add</i></button>
                }
                {props.bidIntegration != null && !props.isNew &&
                    <button className="link-button" onClick={() => props.onRemove()}><i className="material-icons">delete</i></button>
                }
            </Card.Title>
            {props.bidIntegration && <div>
                <div>
                    <Form.Label>Partner</Form.Label>
                    <Form.Control as="select" 
                        value={props.bidIntegration.partner}
                        disabled={!props.isNew}
                        onChange={ev => update({...props.bidIntegration, partner: Number.parseInt(ev.target.value)})}>
                        {props.bidIntegration.partner == null && <option>--Select Partner---</option>}
                        {props.partners.map(m => <option key={m} value={m}>{getPartnerName(m)}</option>)}
                    </Form.Control>
                </div>

                <div className="form-inline">
                    <Form.Label className="pr-2">Enable</Form.Label>
                    <Form.Check
                        type="switch"
                        id="enable-bid-import-switch"
                        label=""
                        checked={props.bidIntegration.isEnabled}
                        onChange={ev => update({...props.bidIntegration, isEnabled: ev.target.checked})}
                    />
                </div>

                <BidConfiguration
                    bidIntegration={props.bidIntegration}
                    onUpdate={config => update({...props.bidIntegration, bidConfiguration: config})}
                />
                {!props.isNew && <AsyncButton onClickAsync={onImportBids}>Import Bids</AsyncButton>}
                
            </div>}
        </Card.Body>
        {props.bidIntegration && (props.isNew || hasChanges) && <Card.Footer className="text-right">
            <Button variant="secondary" className="mr-2" onClick={onCancel}>Cancel</Button>
            <AsyncButton disabled={!canSave()} onClickAsync={onSave}>Save</AsyncButton>
        </Card.Footer>}
    </Card>

}

interface BidConfigurationProps {
    bidIntegration: BidIntegration;
    onUpdate(configuration: any): void;
}

const BidConfiguration: React.FC<BidConfigurationProps> = props => {
    switch (props.bidIntegration.partner) {
        case Partner.Agvance:
            return <AgvanceBidConfiguration {...props} />;
        default:
            return <></>;
    }
}

const AgvanceBidConfiguration: React.FC<BidConfigurationProps> = props => {
    return <>
    <div className="form-inline">
        <Form.Label className="pr-2">Use bid without location to set for all <OverlayTrigger placement="right" overlay={UseBidWithoutLocationPopover}>
            <i className="fa fa-info-circle pl-2"></i>
        </OverlayTrigger></Form.Label>
        <Form.Check
            type="switch"
            id="use-bid-without-location-to-set-for-all-switch"
            checked={props.bidIntegration.bidConfiguration?.useBidWithoutLocationToSetForAll}
            onChange={ev => props.onUpdate({...props.bidIntegration.bidConfiguration, useBidWithoutLocationToSetForAll: ev.target.checked})}
        />
    </div>
    <div className="form-inline">
        <Form.Label className="pr-2">Use bid criteria to determine location <OverlayTrigger placement="right" overlay={UseSecondaryIdToDetermineLocationPopover}>
            <i className="fa fa-info-circle pl-2"></i>
        </OverlayTrigger></Form.Label>
        <Form.Check
            type="switch"
            id="use-secondary-id-to-determine-location-switch"
            checked={props.bidIntegration.bidConfiguration?.useSecondaryIdToDetermineLocation}
            onChange={ev => props.onUpdate({...props.bidIntegration.bidConfiguration, useSecondaryIdToDetermineLocation: ev.target.checked})}
        />
    </div>
    </>
}

const UseBidWithoutLocationPopover = (
    <Popover id="use-bid-without-location-popover">
        <Popover.Content>
            <p>Enable this toggle if the ERP does not uses location to set their bids and is common for all locations, like Wanstead.</p>
        </Popover.Content>
    </Popover>
)

const UseSecondaryIdToDetermineLocationPopover = (
    <Popover id="use-bid-criteria-to-determine-location-popover">
        <Popover.Content>
            <p>Enable this toggle if the ERP uses storing bids against DS locations in bid criteria, like River Valley.</p>
        </Popover.Content>
    </Popover>
)

export default ManageBidIntegration;